import { useCallback, useRef, useState } from 'react';
import { useAsyncAction } from 'legacy-components/form/action/AsyncAction';
import { from } from 'rxjs';
import UnifiedAPIService, { UnifiedAPI } from 'services/UnifiedAPIService';
import { sanitizeText } from '../../../product-forms/FileXSSRemover';
import { useSelector } from 'react-redux';
import { ProductFormState } from '../../../../../../store/types';
import { FEATURE_FLAGS } from '../constants';
import { resolveAPIErrorMessage } from 'modus/components/error-message/ErrorMessage';

// Define types for the hook return value
export interface ProductFormAPIHook {
  createProductState: any; // This would be typed based on AsyncActionState
  editProductState: any; // This would be typed based on AsyncActionState
  createNewProduct: () => Promise<any>;
  updateExistingProduct: (productName: string, payloadOverrides?: { visibility?: string; approvalFlow?: string }) => Promise<any>;
  isLoading: boolean;
  hasError: boolean;
  error: any;
  resetError: () => void;
}

/**
 * Custom hook for Product Form API operations
 * @returns {ProductFormAPIHook} API operations and their states
 */
export const useProductFormAPI = (): ProductFormAPIHook => {
  
  // Get form data from Redux store
  const formData = useSelector((state: { productForm: ProductFormState }) => state.productForm);
  
  // Local state to suppress error display after reset
  const [errorSuppressed, setErrorSuppressed] = useState(false);

  // Helper to build API applicationTypes from form (dynamic codes + redirectUrls per type)
  // When SHOW_EXTENSION_TYPE is false, EXTENSION is excluded from payload (not sent to API)
  const buildApplicationTypesPayload = (): { type: string; redirectUrls?: string[] }[] => {
    const codes = FEATURE_FLAGS.SHOW_EXTENSION_TYPE
      ? formData.selectedApplicationTypeCodes.value
      : formData.selectedApplicationTypeCodes.value.filter((code) => code !== 'EXTENSION');
    return codes.map((code) => {
      const urls = formData.redirectUrlsByType.value[code]?.filter(u => u?.trim()) ?? [];
      return {
        type: code,
        ...(urls.length > 0 ? { redirectUrls: urls } : {})
      };
    });
  };

  // Helper function to build the API payload
  // Uses applicationTypes (new API shape) when feature flag is enabled
  const buildCreatePayload = () => {
    const basePayload = {
      name: formData.name.value.trim(),
      displayName: sanitizeText(formData.name.value.trim()),
      longDescription: sanitizeText(formData.description.value.trim()),
      visibility: formData.visibility.value,
      approvalType: formData.approvalFlow.value === 'open' ? 'AUTO' : 'MANUAL'
    };

    if (FEATURE_FLAGS.PRODUCT_METADATA_CONFIG) {
      return {
        ...basePayload,
        applicationTypes: buildApplicationTypesPayload()
      };
    }

    return basePayload;
  };

  const buildEditPayload = (overrides?: { visibility?: string; approvalFlow?: string }) => {
    const visibility = overrides?.visibility ?? formData.visibility.value;
    const approvalFlow = overrides?.approvalFlow ?? formData.approvalFlow.value;
    const basePayload = {
      displayName: sanitizeText(formData.name.value.trim()),
      longDescription: sanitizeText(formData.description.value.trim()),
      visibility,
      approvalType: approvalFlow === 'open' ? 'AUTO' : 'MANUAL'
    };

    if (FEATURE_FLAGS.PRODUCT_METADATA_CONFIG) {
      return {
        ...basePayload,
        applicationTypes: buildApplicationTypesPayload()
      };
    }

    return basePayload;
  };

  // Create product API call - function that returns an observable
  const [createProductState, { subscribe: createProduct }] = useAsyncAction(() => 
    from(UnifiedAPIService.post(UnifiedAPI.product_api, buildCreatePayload()))
  );

  // Ref for make-public flow so PUT uses correct payload before next render
  const editPayloadOverridesRef = useRef<{ visibility?: string; approvalFlow?: string } | undefined>(undefined);

  // Edit product API call - function that returns an observable
  const [editProductState, { subscribe: editProduct }] = useAsyncAction(() => 
    from(UnifiedAPIService.put(
      UnifiedAPI.parseRequestURL(UnifiedAPI.edit_product_api, { id: formData.product?.name || formData.name.value }),
      buildEditPayload(editPayloadOverridesRef.current)
    ))
  );

  /**
   * Create a new product
   * @returns Create product promise
   */
  const createNewProduct = useCallback(async (): Promise<any> => {
    setErrorSuppressed(false); // Clear suppression on new request
    return await createProduct();
  }, [createProduct]);

  /**
   * Update an existing product
   * @param productName - Existing product name
   * @param payloadOverrides - Optional overrides (e.g. { visibility: 'public', approvalFlow: 'open' }) when state may not be updated yet
   * @returns Update product promise
   */
  const updateExistingProduct = useCallback(async (productName: string, payloadOverrides?: { visibility?: string; approvalFlow?: string }): Promise<any> => {
    setErrorSuppressed(false); // Clear suppression on new request
    editPayloadOverridesRef.current = payloadOverrides;
    try {
      return await editProduct();
    } finally {
      editPayloadOverridesRef.current = undefined;
    }
  }, [editProduct]);

  /**
   * Reset error state - call this when closing the drawer
   */
  const resetError = useCallback(() => {
    setErrorSuppressed(true);
  }, []);

  // Get error message from API response using utility function
  const getErrorMessage = () => {
    const errorValue = createProductState.errorValue || editProductState.errorValue;
    if (!errorValue) return null;
    return resolveAPIErrorMessage(errorValue);
  };

  return {
    // States
    createProductState,
    editProductState,
    
    // Methods
    createNewProduct,
    updateExistingProduct,
    resetError,
    
    // Loading states
    isLoading: createProductState.loading || editProductState.loading,
    hasError: !errorSuppressed && !!(createProductState.error || editProductState.error),
    error: errorSuppressed ? null : getErrorMessage()
  };
};